import { get, isEmpty } from 'lodash';
import createCachedSelector from 're-reselect';
import { compose } from 'redux';
import { createSelector } from 'reselect';

import { BreadCrumb } from '../../components/EditBreadCrumbs/EditBreadCrumbs';
import { PixelsEditorShowsCountersErrors } from '../../components/PixelsEditor/PixelsEditor';
import { DEFAULT_SITELINKS_COUNT } from '../../constants';
import { BannersBaseInfoEditorStateProps } from '../../containers/BannersBaseInfoEditorContainer/BannersBaseInfoEditorContainer';
import { RoutesPath } from '../../routes/index';
import { BannerSummaryErrorParams } from '../../service/BannerEditErrors/ErrorsTextMapper/ErrorsTextMapper';
import {
    skipSharpSign,
    strCounterWithoutNarrow
} from '../../service/BannerEditValidation/helpers';
import {
    checkIsSiteLinkFilled,
    getBannersEditMassActions,
    getMeasurers,
    getSiteLinksCommonTitleLength
} from '../../service/BannerHelpers/BannerHelpers';
import { LangUtils } from '../../service/Lang/Lang';
import { RegionsHelpers } from '../../service/RegionsHelpers/RegionsHelpers';
import {
    BannerConstants,
    BannerId,
    BannerSiteLinksId,
    SiteLinkData,
    SiteLinkInfo
} from '../../typings/banners';
import {
    CalloutEditorItem,
    DraftBanner,
    EditedBanner,
    EditedBannerTurbolanding,
    EditedBannerTypeText,
    getEditedCpmBanner,
    GroupBanners,
    Measurers,
    MeasurersForDisplay,
    PromocodesRestrictedDomains,
    TurbolandingType
} from '../../typings/bannersEditState';
import {
    AccessRights,
    EditorPlaceId,
    ErrorsEdit,
    Region,
    ValidationErrorData,
    ValidationErrorsData,
    ValidationResultItem
} from '../../typings/common';
import { AdvType, AdvTypeClient } from '../../typings/domain';
import {
    GdBannerMeasurerSystem,
    GdGeotreeType,
    OperatorFeaturesFragment
} from '../../typings/graphql-grid';
import { GridViewType } from '../../typings/grid';
import { BannersEditMassActionItems } from '../../typings/groups';
import { ClientFeatures, Regions, State } from '../../typings/state';

export const getEditedBannerCacheKey = (
    state: State,
    bannerId: BannerId
): BannerId => bannerId;

export const getEditorPlaceIdCacheKey = (
    state: State,
    id: EditorPlaceId
): EditorPlaceId => id;

export const selectBanners = (state: State): Record<BannerId, EditedBanner> =>
    state.editBanners.banners;

export const selectPromocodes = (state: State): PromocodesRestrictedDomains =>
    state.editBanners.promocodes;

export const selectEditedBanner = (
    state: State,
    bannerId: string
): EditedBanner => state.editBanners.banners[bannerId];

export const selectEditedDraftBanner = (
    state: State,
    bannerId: BannerId
): DraftBanner => state.editBanners.bannersDraft[bannerId];

export const getTurbolandingType = (
    bannerType: AdvType | AdvTypeClient
): TurbolandingType => {
    switch (bannerType) {
        case AdvType.CPM_GEOPRODUCT:
            return TurbolandingType.METRO;
        default:
            return TurbolandingType.DEFAULT;
    }
};

export const bannerProp = <K extends keyof EditedBanner>(
    propKey: K
): ((state: State, bannerId: BannerId) => EditedBanner[K]) => {
    return compose(
        x => x[propKey],
        selectEditedBanner
    );
};

export const selectEditedBannerErrors = (
    state: State,
    bannerId: BannerId
): ValidationErrorsData => selectEditedBanner(state, bannerId).errors || {};

export const selectEditedDraftBannerErrors = (
    state: State,
    bannerId: BannerId
): ErrorsEdit => selectEditedDraftBanner(state, bannerId).errors || {};

export const errorProp = <K extends keyof ValidationErrorsData>(
    propKey: K
): ((state: State, bannerId: BannerId) => ValidationErrorsData[K]) => {
    return compose(
        x => x[propKey],
        selectEditedBannerErrors
    );
};

export const draftErrorProp = <K extends keyof ErrorsEdit>(
    propKey: K
): ((state: State, bannerId: BannerId) => ErrorsEdit[K]) => {
    return compose(
        x => x[propKey],
        selectEditedDraftBannerErrors
    );
};

export const selectMapByBannerType = createSelector(
    (state: State) => state.editBanners.banners,
    (state: State) => state.grid.selectionInfo.banners.length,
    (state: State) => state.router.route,
    (
        banners: Record<BannerId, EditedBanner>,
        bannersCount: number,
        route: RoutesPath
    ): Record<AdvType, number> => {
        const keys = Object.keys(banners);

        return keys.reduce(
            (mapBanner, key) => {
                const banner: EditedBanner = banners[key];
                const groupType =
                    banner.bannerType === AdvTypeClient.TEXT_MOBILE
                        ? AdvType.TEXT
                        : (banner.bannerType as AdvType);

                if (
                    banner.isEditAllow &&
                    !banner.markedForDeletion &&
                    ![AdvType.CPM_OUTDOOR, AdvType.CPM_INDOOR].includes(
                        groupType
                    )
                ) {
                    mapBanner[groupType] = mapBanner[groupType]
                        ? mapBanner[groupType] + 1
                        : 1;
                }
                return mapBanner;
            },
            <Record<AdvType, number>>{}
        );
    }
);

export const selectEditableTextAdsCount = createSelector(
    (state: State) => state.editBanners.banners,
    (state: State) => state.grid.selectionInfo.banners.length,
    (state: State) => state.router.route,
    (
        banners: Record<BannerId, EditedBanner>,
        bannersCount: number,
        route: RoutesPath
    ) => {
        if (route === RoutesPath.grid) {
            return bannersCount;
        }

        const keys = Object.keys(banners);

        return keys.filter(key => {
            const banner: EditedBanner = banners[key];

            return (
                banner.isEditAllow &&
                !banner.markedForDeletion &&
                (banner.bannerType === AdvType.TEXT ||
                    banner.bannerType === AdvTypeClient.TEXT_MOBILE)
            );
        }).length;
    }
);

export const selectEditableCPCVideoAdsCount = createSelector(
    (state: State) => state.editBanners.banners,
    (banners: Record<BannerId, EditedBanner>) => {
        const keys = Object.keys(banners);

        return keys.filter(key => {
            const banner: EditedBanner = banners[key];

            return (
                !banner.markedForDeletion &&
                banner.isEditAllow &&
                banner.bannerType === AdvType.CPC_VIDEO
            );
        }).length;
    }
);

export const selectEditableCPMAudioAdsCount = createSelector(
    (state: State) => state.editBanners.banners,
    (banners: Record<BannerId, EditedBanner>) => {
        const keys = Object.keys(banners);

        return keys.filter(key => {
            const banner: EditedBanner = banners[key];

            return (
                !banner.markedForDeletion &&
                banner.isEditAllow &&
                banner.bannerType === AdvType.CPM_AUDIO
            );
        }).length;
    }
);

export const selectEditableCPMVideoCount = createSelector(
    (state: State) => state.editBanners.banners,
    (banners: Record<BannerId, EditedBanner>) => {
        const keys = Object.keys(banners);

        return keys.filter(key => {
            const banner: EditedBanner = banners[key];

            return (
                !banner.markedForDeletion &&
                banner.isEditAllow &&
                banner.bannerType === AdvType.CPM_VIDEO
            );
        }).length;
    }
);

export const selectEditableCPMAudioCount = createSelector(
    (state: State) => state.editBanners.banners,
    (banners: Record<BannerId, EditedBanner>) => {
        const keys = Object.keys(banners);

        return keys.filter(key => {
            const banner: EditedBanner = banners[key];

            return (
                !banner.markedForDeletion &&
                banner.isEditAllow &&
                banner.bannerType === AdvType.CPM_AUDIO
            );
        }).length;
    }
);

export const selectEditableMcBannerCount = createSelector(
    (state: State) => state.editBanners.banners,
    (banners: Record<BannerId, EditedBanner>) => {
        const keys = Object.keys(banners);

        return keys.filter(key => {
            const banner: EditedBanner = banners[key];

            return (
                !banner.markedForDeletion &&
                banner.isEditAllow &&
                banner.bannerType === AdvType.MCBANNER
            );
        }).length;
    }
);

export const selectEditableCPMBannerCount = createSelector(
    (state: State) => state.editBanners.banners,
    (banners: Record<BannerId, EditedBanner>) => {
        const keys = Object.keys(banners);

        return keys.filter(key => {
            const banner: EditedBanner = banners[key];

            return (
                !banner.markedForDeletion &&
                banner.isEditAllow &&
                banner.bannerType === AdvType.CPM_BANNER
            );
        }).length;
    }
);

export const selectEditableImageAdsCount = createSelector(
    (state: State) => state.editBanners.banners,
    (banners: Record<BannerId, EditedBanner>) => {
        const keys = Object.keys(banners);

        return keys.filter(key => {
            const banner: EditedBanner = banners[key];

            return (
                !banner.markedForDeletion &&
                banner.isEditAllow &&
                banner.bannerType === AdvType.IMAGE_AD
            );
        }).length;
    }
);

export const selectBannersMassActionItems = createSelector(
    selectMapByBannerType,
    (state: State) => state.commonData.operatorFeatures.isAdsMassEditAllowed,
    (
        bannersMap: Record<AdvType, number>,
        isAdsMassEditAllowed: boolean
    ): BannersEditMassActionItems => {
        const total = Object.keys(bannersMap).reduce(
            (summ, key) => summ + bannersMap[key],
            0
        );

        if (isAdsMassEditAllowed && total > 1) {
            return getBannersEditMassActions(bannersMap);
        }

        return [];
    }
);

export const selectBannersSummaryErrors = createSelector(
    (state: State) => state.editBanners.errorsSummary,
    (errorsSummary?: BannerSummaryErrorParams): BannerSummaryErrorParams => {
        if (errorsSummary) {
            return errorsSummary;
        }

        return {};
    }
);

export const selectBannerBaseInfo = createCachedSelector(
    (state: State, bannerId: BannerId) =>
        (state.editBanners.banners[bannerId] as EditedBannerTypeText).title,
    (state: State, bannerId: BannerId) =>
        (state.editBanners.banners[bannerId] as EditedBannerTypeText)
            .titleExtension,
    (state: State, bannerId: BannerId) =>
        (state.editBanners.banners[bannerId] as EditedBannerTypeText).body,
    errorProp('title'),
    errorProp('titleExtension'),
    errorProp('body'),
    state => state.editBanners.constants,
    (
        title,
        titleExtension,
        body,
        titleErrors,
        titleExtensionErrors,
        bodyErrors,
        constants
    ): BannersBaseInfoEditorStateProps => {
        if (
            title === undefined ||
            titleExtension === undefined ||
            body === undefined
        ) {
            throw new Error('');
        }

        const errors = {
            title: (titleErrors && titleErrors.errors) || [],
            titleExtension:
                (titleExtensionErrors && titleExtensionErrors.errors) || [],
            body: (bodyErrors && bodyErrors.errors) || []
        };
        const counters = {
            title: constants.ad.maxLengthTitle,
            titleExtension: constants.ad.maxLengthTitleExtension,
            body: constants.ad.maxLengthBody
        };

        return {
            baseInfo: {
                mainTitle: {
                    text: title,
                    count: strCounterWithoutNarrow(
                        skipSharpSign(title),
                        counters.title,
                        constants.ad.narrowSymbols
                    ),
                    valid: errors.title.length === 0,
                    errors: errors.title
                },
                additionalTitle: {
                    text: titleExtension,
                    count: strCounterWithoutNarrow(
                        skipSharpSign(titleExtension),
                        counters.titleExtension,
                        constants.ad.narrowSymbols
                    ),
                    valid: errors.titleExtension.length === 0,
                    errors: errors.titleExtension
                },
                description: {
                    text: body,
                    count: strCounterWithoutNarrow(
                        skipSharpSign(body),
                        counters.body,
                        constants.ad.narrowSymbols
                    ),
                    valid: errors.body.length === 0,
                    errors: errors.body
                }
            }
        };
    }
)(getEditedBannerCacheKey);

export const calcBreadcrumbsFromGroupData = createCachedSelector(
    (groupedBanners: GroupBanners, groupId: string) => groupedBanners,
    (groupedBanners: GroupBanners): Array<BreadCrumb> => {
        return [
            {
                name: groupedBanners.campaign.name,
                id: groupedBanners.campaign.id,
                view: 'campaigns' as GridViewType
            },
            {
                name: groupedBanners.groupName,
                id: groupedBanners.groupId,
                view: 'groups' as GridViewType
            }
        ];
    }
)((groupedBanners: GroupBanners, groupId: string) => groupId);

export const selectEditableBannerTypes = createSelector(
    (state: State) => state.commonData.operatorFeatures,
    (state: State) => state.commonData.accessRights,
    (features: OperatorFeaturesFragment, accessRights: AccessRights) => {
        return getEditableBannerTypes(features, accessRights);
    }
);

export const getEditableBannerTypes = (
    features: OperatorFeaturesFragment,
    accessRights: AccessRights
): Array<AdvType | AdvTypeClient> => {
    const bannerTypes: Array<AdvType | AdvTypeClient> = [];

    if (features.isBannerUpdateAllowed) {
        bannerTypes.push(AdvType.TEXT);
        bannerTypes.push(AdvTypeClient.TEXT_MOBILE);
        bannerTypes.push(AdvType.IMAGE_AD);
        bannerTypes.push(AdvType.CPC_VIDEO);
    }

    if (features.isPerformanceAdEditAllowed) {
        bannerTypes.push(AdvType.PERFORMANCE);
    }

    if (features.isCpmBannerUpdateAllowed) {
        bannerTypes.push(AdvType.CPM_BANNER);
        bannerTypes.push(AdvType.CPM_VIDEO);
        bannerTypes.push(AdvType.CPM_GEOPRODUCT);
        bannerTypes.push(AdvType.CPM_OUTDOOR);
        bannerTypes.push(AdvType.CPM_INDOOR);
        bannerTypes.push(AdvType.CPM_AUDIO);
    }

    if (accessRights && accessRights.canEditInternalAds) {
        bannerTypes.push(AdvType.INTERNAL);
    }

    if (features.isMcBannerDnaEnabled) {
        bannerTypes.push(AdvType.MCBANNER);
    }

    return bannerTypes;
};

export const getRegionsFlatMapSelector = createSelector(
    (state: State) => state.commonData.regions,
    (state: State) => state.commonData.client.translocalGeotreeType,
    (regions: Regions, translocalGeotreeType?: GdGeotreeType) => {
        const regionTree: Region[] = regions
            ? RegionsHelpers.getRegionsTree(
                  regions,
                  LangUtils.getCurrentLang(),
                  translocalGeotreeType
              )
            : [];

        return RegionsHelpers.flattenRegions({}, regionTree);
    }
);

export const selectBannerPixelsErrors = createCachedSelector(
    errorProp('pixels'),
    (allErrors = {}) => {
        const result: {
            showsCounters?: PixelsEditorShowsCountersErrors;
            pixel?: ValidationErrorData[];
            pixelProviders?: ValidationResultItem[];
        } = {};

        const showsCountersErrors: PixelsEditorShowsCountersErrors = {};

        Object.keys(allErrors)
            .map(key =>
                key === 'errors' ? allErrors[key] : allErrors[key].errors
            )
            .map(error => {
                const { code = '', path = '' } = (error && error[0]) || {};

                switch (code) {
                    case 'GridDefectIds.Pixels.INVALID_AUDIENCE_PIXEL_FORMAT': {
                        result.pixel = error;
                        break;
                    }

                    case 'BannerDefectIds.PixelProviderLimits.EQUAL_PIXEL_PROVIDERS': {
                        result.pixelProviders = error;
                        break;
                    }

                    case 'BannerDefectIds.PixelPermissions.NO_RIGHTS_TO_PIXEL':
                    case 'GridDefectIds.Pixels.INVALID_AUDIT_PIXEL_FORMAT': {
                        const showsCountersErrorsIndex = path.slice(-1);
                        showsCountersErrors[showsCountersErrorsIndex] = error;

                        break;
                    }

                    default:
                        break;
                }
            });

        if (!isEmpty(showsCountersErrors)) {
            result.showsCounters = showsCountersErrors;
        }

        return result;
    }
)(getEditedBannerCacheKey);

export const selectBannerMeasurersErrors = createCachedSelector(
    errorProp('measurers'),
    (allErrors = {}) => {
        return Object.keys(allErrors).reduce((acc, key) => {
            acc[key] = allErrors[key].errors;
            return acc;
        }, []);
    }
)(getEditedBannerCacheKey);

export const selectorBannerMeasurersValues = createCachedSelector(
    (state: State, bannerId: BannerId) => {
        const banner = getEditedCpmBanner(state.editBanners.banners[bannerId]);
        return (banner && banner.measurers) || [];
    },
    (measurers: Measurers[]) => {
        return measurers;
    }
)(getEditedBannerCacheKey);

export const selectAllAvailableMeasurers = createCachedSelector(
    (state: State, bannerId: BannerId) => state.commonData.features,
    (state: State, bannerId: BannerId) => state.editBanners.banners[bannerId],
    (features: ClientFeatures, banner?: EditedBanner) => {
        return banner && getMeasurers(banner.bannerType, features);
    }
)(getEditedBannerCacheKey);

export const selectBannerMeasurers = createSelector(
    selectBannerMeasurersErrors,
    selectorBannerMeasurersValues,
    selectAllAvailableMeasurers,
    (
        measurersErrors: (ValidationErrorData[] | undefined)[],
        measurersValues: Measurers[],
        availableMeasurers: GdBannerMeasurerSystem[] = []
    ): MeasurersForDisplay[] => {
        return availableMeasurers.map(val => {
            const valIndex = measurersValues.findIndex(
                measurer => measurer.measurerSystem === val
            );
            return {
                measurerSystem: val,
                params: valIndex > -1 ? measurersValues[valIndex].params : '',
                isSelected: valIndex > -1,
                errors: measurersErrors[valIndex]
            };
        });
    }
);

export const isMeasurersEditorAvailable = createSelector(
    selectAllAvailableMeasurers,
    (measurers: GdBannerMeasurerSystem[] = []) => Boolean(measurers.length)
);

const selectBannerTnsIdErrors = createCachedSelector(
    errorProp('tnsId'),
    (errors = {}) => errors.errors || []
)(getEditedBannerCacheKey);

export const selectBannerTnsId = createSelector(
    selectBannerTnsIdErrors,
    (state: State, bannerId: BannerId) => state.editBanners.banners[bannerId],
    (errors: ValidationErrorData[], banner: EditedBanner) => {
        switch (banner.bannerType) {
            case AdvType.CPM_VIDEO:
            case AdvType.CPM_BANNER:
                return { value: banner.tnsId || '', errors };
            default:
                return { value: '', errors };
        }
    }
);

export const selectSiteLinks = createSelector(
    (state: State, bannerId: BannerSiteLinksId) =>
        state.siteLinksEditor.data[bannerId].items,
    (state: State, bannerId: BannerSiteLinksId) =>
        state.siteLinksEditor.data[bannerId].errors,
    state => state.editBanners.constants,
    (
        siteLinks: SiteLinkData[],
        sitelinkErrors: Record<string, ValidationErrorData[]>,
        constants: BannerConstants
    ) => {
        const siteLinksFilled = siteLinks.filter(checkIsSiteLinkFilled);

        const counters = {
            title: constants.sitelinks.maxLengthSitelinkTitle,
            link: constants.sitelinks.maxLengthSitelinkHref,
            descr: constants.sitelinks.maxLengthSitelinkDescr
        };

        let commonTitleLengthFirstBlock = 0;
        let commonTitleLengthSecondBlock: number | null = null;

        if (siteLinksFilled.length > DEFAULT_SITELINKS_COUNT) {
            commonTitleLengthFirstBlock = getSiteLinksCommonTitleLength(
                siteLinksFilled.slice(0, DEFAULT_SITELINKS_COUNT)
            );
            commonTitleLengthSecondBlock = getSiteLinksCommonTitleLength(
                siteLinksFilled.slice(DEFAULT_SITELINKS_COUNT)
            );
        } else {
            commonTitleLengthFirstBlock = getSiteLinksCommonTitleLength(
                siteLinks
            );
        }

        return siteLinks.map(
            (siteLinkData: SiteLinkData, index: number): SiteLinkInfo => {
                const errorsText = {
                    title: get(sitelinkErrors, `[${index}].title.errors`, []),
                    link: get(sitelinkErrors, `[${index}].link.errors`, []),
                    descr: get(sitelinkErrors, `[${index}].descr.errors`, [])
                };

                let titleLimit = counters.title;
                let isAvailableLengthLessThanLimit = true;
                let availableSymbolsCount;

                if (
                    index < DEFAULT_SITELINKS_COUNT ||
                    commonTitleLengthSecondBlock === null
                ) {
                    availableSymbolsCount =
                        constants.sitelinks.maxLengthSitelinksTitle -
                        commonTitleLengthFirstBlock;
                } else {
                    availableSymbolsCount =
                        constants.sitelinks.maxLengthSitelinksTitle -
                        (commonTitleLengthSecondBlock || 0);
                }

                if (
                    availableSymbolsCount + siteLinkData.title.length <
                    counters.title
                ) {
                    titleLimit = availableSymbolsCount;
                    isAvailableLengthLessThanLimit = false;
                }

                return {
                    title: {
                        limit: isAvailableLengthLessThanLimit
                            ? titleLimit - siteLinkData.title.length
                            : titleLimit,
                        countFirstBlock:
                            constants.sitelinks.maxLengthSitelinksTitle -
                            commonTitleLengthFirstBlock,
                        countSecondBlock:
                            commonTitleLengthSecondBlock !== null
                                ? constants.sitelinks.maxLengthSitelinksTitle -
                                  commonTitleLengthSecondBlock
                                : undefined,
                        text: siteLinkData.title,
                        valid: errorsText.title.length === 0,
                        errors: errorsText.title,
                        isMassActionValuesDifferent:
                            siteLinkData.isMassActionsTitleDifferent
                    },
                    link: {
                        count:
                            counters.link -
                            siteLinkData.link.length -
                            siteLinkData.protocol.length,
                        text: siteLinkData.link,
                        valid: errorsText.link.length === 0,
                        errors: errorsText.link,
                        isUrlChecking: siteLinkData.isUrlChecking,
                        isMassActionValuesDifferent:
                            siteLinkData.isMassActionsLinkDifferent
                    },
                    protocol: siteLinkData.protocol,
                    descr: {
                        count: counters.descr - siteLinkData.descr.length,
                        text: siteLinkData.descr,
                        valid: errorsText.descr.length === 0,
                        errors: errorsText.descr,
                        isMassActionValuesDifferent:
                            siteLinkData.isMassActionsDescrDifferent
                    },
                    turbo: {
                        data: siteLinkData.turbo!,
                        isLoading: siteLinkData.isTurboLoading,
                        isMassActionValuesDifferent:
                            siteLinkData.isMassActionsTurboDifferent
                    },
                    isMassActionTurboValuesDifferent:
                        siteLinkData.isMassActionsTurboDifferent,
                    isPreparedSiteLinkFromMassAction:
                        siteLinkData.isPreparedSiteLinkFromMassAction
                };
            }
        );
    }
);

export const selectTurbolandingInfo = createCachedSelector(
    (state: State, turbolandingId: EditorPlaceId) =>
        state.turbolandings[turbolandingId],
    (
        state: State,
        turbolandingId: EditorPlaceId,
        hrefParamsErrors?: ValidationErrorData[]
    ) => hrefParamsErrors,
    (
        turbolanding: EditedBannerTurbolanding,
        hrefParamsErrors?: ValidationErrorData[]
    ): EditedBannerTurbolanding => {
        if (!turbolanding) {
            return {};
        }

        return {
            expanded: turbolanding.expanded,
            page: turbolanding.page,
            trackParams: turbolanding.trackParams,
            hrefParams: turbolanding.hrefParams,
            loading: turbolanding.loading,
            templateId: turbolanding.templateId,
            isCanvasOpened: turbolanding.isCanvasOpened,
            hrefParamsErrors
        };
    }
)((state: State, turbolandingId: EditorPlaceId) => turbolandingId);

export const selectMarkedCalloutsLettersSum = createSelector(
    (state: State) => state.calloutsEditor.list,
    (state: State, bannerId: BannerId) =>
        state.calloutsEditor.data[bannerId].selected,
    (list: CalloutEditorItem[], selected: string[]) => {
        const selectedCallout = list.filter(item => selected.includes(item.id));

        return selectedCallout.reduce((sum, callout) => {
            return sum + callout.text.length;
        }, 0);
    }
);
